/* import __COLOCATED_TEMPLATE__ from './team-select.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { generateSafeEmoji } from 'embercom/lib/emoji';
import { captureMessage } from 'embercom/lib/sentry';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type Admin from 'embercom/models/admin';

interface TeamSelectItem {
  text?: string;
  value: string | null;
  isSelected?: boolean;
  isDisabled?: boolean;
  component: string;
  componentAttrs?: {
    teamAvatar: string;
  };
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    selectedTeams: string[];
    onSelectionChanged: (newSelection: string[]) => void;
    isDisabled?: boolean;
    onPendingSelectionChange?: (hasPending: boolean) => void;
  };
}

export default class TeamSelect extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @tracked _pendingSelection: string[] | null = null;

  private generateTeamAvatarEmoji(team: Admin): string {
    let emoji = team.avatar_emoji;

    if (!emoji) {
      captureMessage('team-select.avatar_emoji-missing', {
        fingerprint: ['team-select', 'avatar_emoji', 'missing'],
        extra: {
          teamId: team.id,
          teamName: team.name,
          emojiValue: emoji,
          emojiType: typeof emoji,
          isRedacted: team.is_redacted,
          isTeam: team.is_team,
          isAppTeam: team.is_app_team,
          component: 'team-select',
          appId: this.appService.app?.id,
        },
        tags: {
          responsible_team: 'team-inbox',
          responsibleTeam: 'team-inbox',
          issue_type: 'data_integrity',
          field: 'avatar_emoji',
        },
      });
      return '';
    }
    return generateSafeEmoji(emoji);
  }

  get sortedTeams(): Admin[] {
    return this.appService.app.teams.sortBy('name') as Admin[];
  }

  // Returns selectedTeams or empty array. Can be null/undefined when a team is deleted
  // or if the parent component passes null before data loads.
  get selectedTeamsOrEmpty(): string[] {
    if (this.args.selectedTeams == null) {
      captureMessage('team-select.selectedTeams-null', {
        fingerprint: ['team-select', 'selectedTeams', 'null'],
        extra: {
          selectedTeamsValue: this.args.selectedTeams,
          selectedTeamsType: typeof this.args.selectedTeams,
          component: 'team-select',
          appId: this.appService.app?.id,
        },
        tags: {
          responsible_team: 'team-inbox',
          responsibleTeam: 'team-inbox',
          issue_type: 'data_integrity',
          field: 'selectedTeams',
        },
      });
      return [];
    }
    return this.args.selectedTeams;
  }

  get selectedAndRedactedTeamIds(): string[] {
    return this.sortedTeams
      .filter((team) => this.selectedTeamsOrEmpty.includes(team.id) && team.is_redacted)
      .map((team) => team.id);
  }

  get teamSelectItems(): TeamSelectItem[] {
    if (this.sortedTeams.length === 0) {
      return [
        {
          value: null,
          isDisabled: true,
          component: 'settings/saved-replies/team-select-empty-item',
        },
      ];
    }

    let filteredTeams = this.sortedTeams.filter((team) => !team.is_redacted);

    return filteredTeams.map((team) => ({
      text: team.name,
      value: team.id,
      isSelected: this.selectedTeamsOrEmpty.includes(team.id),
      component: 'settings/saved-replies/team-select-item',
      componentAttrs: { teamAvatar: this.generateTeamAvatarEmoji(team) },
    }));
  }

  get openerLabel(): string {
    if (isEmpty(this.selectedTeamsOrEmpty)) {
      return this.intl.t('inbox.manage-macros.team-select.opener-label');
    } else {
      return this.selectedTeamsOrEmpty
        .map((id) => {
          let team = this.sortedTeams.find((team) => parseInt(team.id, 10) === parseInt(id, 10));
          if (team) {
            return `${team.avatar_emoji ?? ''} ${team.name}`.trim();
          } else {
            return this.intl.t('new-settings.helpdesk.macros.deleted-team');
          }
        })
        .join(', ');
    }
  }

  @action selectionChanged(newSelection: string[]): void {
    let hadPending = this._pendingSelection !== null;
    this._pendingSelection = [...newSelection, ...this.selectedAndRedactedTeamIds];
    if (!hadPending) {
      this.args.onPendingSelectionChange?.(true);
    }
  }

  @action onDropdownClosed(): void {
    if (this._pendingSelection !== null) {
      this.args.onSelectionChanged(this._pendingSelection);
      this._pendingSelection = null;
      this.args.onPendingSelectionChange?.(false);
    }
  }

  willDestroy(): void {
    super.willDestroy();
    if (this._pendingSelection !== null) {
      this.args.onSelectionChanged(this._pendingSelection);
      this._pendingSelection = null;
      this.args.onPendingSelectionChange?.(false);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::SavedReplies::TeamSelect': typeof TeamSelect;
    'settings/saved-replies/team-select': typeof TeamSelect;
  }
}
